// Builders for the "healthy" branch of each metric's finding. Pulled out of
// app.js so the giant key-switch can live in its own file and be unit-tested
// without booting the whole app. The three helpers that need to talk to the
// live engine layout (readableProbeLabel, findingCylinderReference,
// capitalizeSentence) are passed in as a context object so this module stays
// pure.

import {
  chtCautionThresholdF,
  chtPeakReviewThresholdF,
  chtCoolingRateTriggerFpm,
  chtHealthReviewThresholdF,
} from "../core/config.js";
import { panelNumber } from "../core/display.js";
import { formatCompactElapsedTime } from "../core/format.js";
import { chtExposureNote, panelPercent } from "../analysis/cht.js";
import { valveFrequencyLabel } from "../analysis/valve.js";

export function detectorCoverageEvidence(metric) {
  const seconds = metric.details?.coverage?.evaluatedSeconds;
  if (!Number.isFinite(seconds)) return [];
  const engines = metric.details?.coverage?.engines;
  const engineRows = Array.isArray(engines) ? engines.map(engine => ({
    label: `${engine.label ?? engine.engineKey} coverage`, value: `${Math.round(engine.evaluatedSeconds)} s`,
    detail: engine.status === "unavailable" ? "Insufficient usable evidence for a clean result on this engine."
      : `${engine.confidence} coverage confidence for this engine.` })) : [];
  return [...engineRows, { label: engineRows.length ? "Minimum engine coverage" : "Evaluated coverage", value: `${Math.round(seconds)} s`,
    detail: metric.details?.confidence?.label === "Low"
      ? "Limited usable coverage lowers confidence. The score reflects the observed condition."
      : "Supported elapsed time; overlapping windows count once. Coverage is separate from the condition score." },
  { label: "Coverage confidence", value: metric.details?.confidence?.label ?? "Low",
    detail: "Confidence in the available screening coverage, not a probability of engine health or a diagnosis." }];
}

export function turboEngineLabels(details: Record<string, any> = {}) {
  const labels = details.engineLabels ?? {};
  return {
    primaryShort: labels.primaryShortLabel ?? "L",
    secondaryShort: labels.secondaryShortLabel ?? "R",
    primary: labels.primaryLabel ?? "Left Engine",
    secondary: labels.secondaryLabel ?? "Right Engine",
  };
}

export function magWindowLabel(candidate) {
  if (!candidate || !Number.isFinite(candidate.startTime)) return "";
  const start = formatCompactElapsedTime(candidate.startTime);
  const end = Number.isFinite(candidate.endTime) ? formatCompactElapsedTime(candidate.endTime) : "";
  return end ? `${start}-${end}` : start;
}

export function metricNearChtCaution(metric) {
  const peak = metric?.details?.max;
  return Number.isFinite(metric?.score)
    && metric.score >= 88
    && Number.isFinite(peak)
    && peak >= chtCautionThresholdF - 10
    && peak < chtCautionThresholdF;
}

export function metricChtPeakAboveCaution(metric) {
  const peak = metric?.details?.max;
  return Number.isFinite(metric?.score)
    && metric.score >= 88
    && Number.isFinite(peak)
    && peak >= chtCautionThresholdF
    && peak < chtPeakReviewThresholdF;
}

export function metricNearCoolingRateTrigger(metric) {
  const rate = metric?.details?.top?.rateFpm;
  const threshold = metric?.details?.thresholdFpm ?? chtCoolingRateTriggerFpm;
  return Number.isFinite(metric?.score)
    && metric.score >= 88
    && Number.isFinite(rate)
    && Number.isFinite(threshold)
    && rate >= threshold * 0.85
    && rate < threshold;
}

export function healthyMetricTitle(key, metric, evidence) {
  if (key === "valve") return "EGT variation: nothing actionable yet";
  if (key === "chtRhythm") return "CHT rhythm: nothing actionable yet";
  if (key === "turbo" && metric.details?.status === "margin-watch") return "TIT near limit: not exceeded";
  if (key === "cht" && metricNearChtCaution(metric)) return "CHT close to caution: not crossed";
  if (key === "cht" && metricChtPeakAboveCaution(metric)) return "CHT peak above caution: not sustained";
  if (key === "coolingRate" && metricNearCoolingRateTrigger(metric)) return "Cooling rate near trigger: not exceeded";
  if (key === "mag") return "Mag check identified";
  if (key === "abnormalCombustion" && metric.details?.egtBalanceCue) return "No CHT corroboration for EGT drop";
  return `${evidence.title} check clear`;
}

export function healthyMetricFinding(key, metric, context) {
  const evidence = healthyMetricEvidence(key, metric, context);
  return {
    title: healthyMetricTitle(key, metric, evidence),
    meta: metric.note,
    metaItems: evidence.metaItems,
    body: evidence.body,
    evidence: evidence.evidence,
    evidenceTitle: evidence.evidenceTitle,
    causes: evidence.causes,
    actions: [
      ["low", "Keep this flight available as a comparison baseline"],
      ["low", "Watch whether this pattern changes in similar future phases"],
    ],
    confidence: evidence.confidence,
  };
}

// On a "clear" sensors card (score >= 88) the worst channel can still carry a
// little sub-threshold suspicion — that is what keeps an otherwise-stable log
// at, say, 93 instead of a clean bill. When that happens we name the channel
// and say plainly what looked off, so the owner is not left guessing why the
// score is not perfect. Returns null when the worst channel is genuinely spotless.
function sensorClearMinorReason(top) {
  if (!top || !(top.suspicion > 0)) return null;
  const label = top.label || "One probe";
  const tail = " — minor and below the review threshold for this screen";
  if (/^implausibleLow(?:Rpm|Map|FuelFlow)$/.test(top.conditionWindow?.kind ?? "")) {
    return `${label} read implausibly low for the engine heat at the time${tail}`;
  }
  if (top.zeroCount) return `${label} logged a few dropout (zero) samples${tail}`;
  if (top.flatline) return `${label} held a flat reading briefly${tail}`;
  if (top.jumpWindow || top.impossibleJumpCount) return `${label} had a few abrupt sample-to-sample jumps${tail}`;
  if (top.coldMismatchCount) return `${label} tracked slightly cooler than its neighbors for part of the run${tail}`;
  if (top.hotMismatchCount) return `${label} tracked slightly warmer than its neighbors for part of the run${tail}`;
  if (top.conditionWindow) return `${label} looked briefly off-pattern${tail}`;
  return `${label} showed a small signal-quality blemish${tail}`;
}

export function healthyMetricEvidence(key, metric, context: Record<string, any> = {}) {
  const {
    readableProbeLabel = () => "",
    findingCylinderReference = () => ({ label: "this cylinder", location: "" }),
    capitalizeSentence = (value) => {
      const text = String(value ?? "");
      return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;
    },
  } = context;

  if (key === "sensors") {
    const channelCount = metric.details.channelCount ?? 0;
    const rowsChecked = metric.details.rowsChecked ?? 0;
    const sensorPurpose = "This screen looks for probe channels whose data does not look trustworthy in this log — readings that drop out, jump impossibly far between samples, sit outside the expected range, or fail to track the other cylinders. When a probe is unreliable, the engine-health screens that depend on it become less reliable too.";
    const minorReason = sensorClearMinorReason(metric.details.top);
    const causes = minorReason
      ? [
          [minorReason, 0.5],
          ["Otherwise channels track their peers and stay in the expected range", 0.34],
          ["Brief intermittent issues can still hide in the raw trace — worth a glance if a related pattern repeats", 0.16],
        ]
      : [
          ["No strong probe-quality issue detected", 0.48],
          ["Per-cylinder channels agree with peer behavior", 0.34],
          ["Brief intermittent issues can still hide in the raw trace — worth a glance if a related pattern repeats", 0.18],
        ];
    return {
      title: "Sensor/probe",
      body: `${sensorPurpose}\n\nThe sensor screen checked ${channelCount} logged signal channel${channelCount === 1 ? "" : "s"} across ${rowsChecked.toLocaleString()} relevant sample${rowsChecked === 1 ? "" : "s"} without strong dropout, jump, flatline, or peer-mismatch evidence in this log.`,
      causes,
      confidence: "Medium",
    };
  }
  if (key === "turbo") {
    const details = metric.details ?? {};
    const labels = turboEngineLabels(details);
    const titLimit = details.limits?.maxTitF;
    const hasPerEngineTit = details.twin && (Number.isFinite(details.peakTitLeft) || Number.isFinite(details.peakTitRight));
    const hasPerEngineMap = details.twin && (Number.isFinite(details.peakMapLeft) || Number.isFinite(details.peakMapRight));
    const isTwinFormat = hasPerEngineTit || hasPerEngineMap;
    const formatTit = (value) => Number.isFinite(value) ? `${Math.round(value)} F` : "n/a";
    const formatMap = (value) => Number.isFinite(value) ? `${value.toFixed(1)} inHg` : "n/a";
    const titBlock = hasPerEngineTit
      ? `\n\nPeak TIT\n${labels.primaryShort} Turbo ${formatTit(details.peakTitLeft)}\n${labels.secondaryShort} Turbo ${formatTit(details.peakTitRight)}`
      : Number.isFinite(details.peakTit) ? ` Peak TIT was ${Math.round(details.peakTit)} F.` : "";
    const mapBlock = hasPerEngineMap
      ? `\n\nPeak MAP\n${labels.primaryShort} Turbo ${formatMap(details.peakMapLeft)}\n${labels.secondaryShort} Turbo ${formatMap(details.peakMapRight)}`
      : Number.isFinite(details.peakMap) ? ` Peak MAP was ${details.peakMap.toFixed(1)} inHg.` : "";
    if (details.status === "margin-watch") {
      const limitInline = Number.isFinite(titLimit) ? ` The configured TIT limit is ${Math.round(titLimit)} F.` : "";
      const limitLine = Number.isFinite(titLimit) ? `\nlimit ${Math.round(titLimit)} F` : "";
      return {
        title: "Turbo/boost",
        body: isTwinFormat
          ? `TIT approached the configured limit but did not exceed it.${titBlock}${limitLine}${mapBlock}`
          : `TIT approached the configured limit but did not exceed it.${titBlock}${limitInline}${mapBlock}`,
        causes: [
          ["TIT stayed below the configured limit", 0.48],
          ["TIT was close enough to the limit to keep watching", 0.32],
          ["Profile limits still determine authoritative interpretation", 0.2],
        ],
        confidence: details.limitCount ? "Medium" : "Low",
      };
    }
    return {
      title: "Turbo/boost",
      body: `Turbo-related channels stayed inside the configured profile limits.${titBlock}${mapBlock}`,
      causes: [
        ["No configured turbo limit exceedance detected", 0.48],
        ["TIT, MAP, and boost context were available where logged", 0.32],
        ["Profile limits still determine authoritative interpretation", 0.2],
      ],
      confidence: details.limitCount ? "Medium" : "Low",
    };
  }
  if (key === "takeoffEnrichment" && metric.details?.status === "evaluated") {
    const details = metric.details ?? {};
    const observed = Number.isFinite(details.observedFuelFlowGph) ? `${details.observedFuelFlowGph.toFixed(1)} gph` : "the logged value";
    const expected = Number.isFinite(details.expectedFuelFlowGph) ? ` against an expected ~${details.expectedFuelFlowGph.toFixed(1)} gph for the power available` : "";
    return {
      title: "Takeoff enrichment",
      body: `The takeoff fuel-flow screen compared sustained fuel flow (${observed})${expected} with the configured target in a corroborated departure window. It met the power-scaled target. This comparison does not establish mixture position, cylinder cooling adequacy, or detonation margin; confirm the target and procedure against the applicable POH/AFM.`,
      causes: [
        ["Full-power fuel flow at or above the configured target", 0.6],
        ["Continuous takeoff or initial-climb samples supported the comparison", 0.25],
        ["Confirm the configured target matches the manufacturer's full-power spec", 0.15],
      ],
      confidence: details.confidence === "high" ? "Medium" : "Low",
    };
  }
  if (key === "oilSystem") {
    const details = metric.details ?? {};
    const pressure = (details.summaries ?? []).find((summary) => summary.kind === "pressure");
    const temp = (details.summaries ?? []).find((summary) => summary.kind === "temperature");
    const pressureText = pressure
      ? ` Oil pressure stayed between ${panelNumber(pressure.min, " psi")} and ${panelNumber(pressure.max, " psi")}.`
      : "";
    const tempText = temp
      ? ` Oil temperature stayed between ${panelNumber(temp.min, " F")} and ${panelNumber(temp.max, " F")}.`
      : "";
    return {
      title: "Oil system",
      body: `The oil-system screen checked running oil pressure and oil temperature samples against the current overview thresholds.${pressureText}${tempText}${details.qualification ? ` ${details.qualification}` : ""}`,
      causes: [
        [details.pressureLimitsKnown === false ? "Generic pressure screen; applicable limits are incomplete" : "No sustained oil-pressure excursion against configured limits", 0.46],
        ["Oil temperature stayed inside the current review range", 0.34],
        ["Monitor channel scaling and POH limits still matter", 0.2],
      ],
      confidence: details.rowsChecked && details.pressureLimitsKnown !== false ? "Medium" : "Low",
    };
  }
  if (key === "coolantMargin") {
    const details = metric.details ?? {};
    const limit = details.maxLimitF ?? 248;
    const maxText = Number.isFinite(details.max) ? ` Coolant peaked at ${panelNumber(details.max, " F")}.` : "";
    const marginText = Number.isFinite(details.marginF) ? ` That left ${panelNumber(details.marginF, " F")} of margin to the ${limit} F limit.` : "";
    return {
      title: "Coolant margin",
      body: `The coolant-margin screen checked running coolant-temperature samples against the current Rotax-style high-temperature limit.${maxText}${marginText}`,
      causes: [
        ["Coolant temperature stayed below the review margin", 0.52],
        ["Running-engine samples had enough coverage to evaluate the margin", 0.28],
        ["Monitor channel scaling and aircraft-specific limits still matter", 0.2],
      ],
      confidence: details.rowsChecked ? "Medium" : "Low",
    };
  }
  if (key === "cht") {
    const peak = Math.round(metric.details.max ?? 0);
    const caution = Math.round(chtCautionThresholdF);
    const reviewThreshold = Math.round(chtPeakReviewThresholdF);
    const chtPurpose = `This screen tracks how much time CHT spent above the ${caution} F caution threshold and how high any peaks went. Sustained time above the caution threshold is a meaningful long-term wear input for valves, rings, and cylinder integrity, so it's the time-above-threshold pattern that this screen cares about more than brief transients.`;
    if (metricNearChtCaution(metric)) {
      return {
        title: "CHT",
        body: `${chtPurpose}\n\nCHT in this log topped out at ${peak} F — close to the ${caution} F caution threshold but not crossing it. Worth keeping an eye on the same metric in nearby flights.`,
        causes: [
          ["CHT stayed below the caution threshold", 0.48],
          ["Peak CHT sat close to the caution threshold — worth watching for repeats", 0.32],
          ["Compare CHT in similar climb/cruise phases across nearby flights", 0.2],
        ],
        confidence: "Medium",
      };
    }
    if (metricChtPeakAboveCaution(metric)) {
      const belowCaution = metric.details.belowCautionThreshold;
      const aboveCautionPercent = Number.isFinite(belowCaution) ? panelPercent(100 - belowCaution) : null;
      const exposureSentence = aboveCautionPercent
        ? `Peak CHT in this log reached ${peak} F, which briefly crossed the ${caution} F caution threshold for about ${aboveCautionPercent} of samples.`
        : `Peak CHT in this log reached ${peak} F, which briefly crossed the ${caution} F caution threshold.`;
      return {
        title: "CHT",
        body: `${chtPurpose}\n\n${exposureSentence} Sustained exposure stayed below the ${reviewThreshold} F review threshold, so this reads as a brief transient rather than the time-above-threshold pattern this screen flags.`,
        causes: [
          [`Peak briefly crossed the ${caution} F caution threshold`, 0.42],
          [`Sustained CHT exposure stayed below the ${reviewThreshold} F review threshold`, 0.32],
          ["Worth comparing CHT in similar climb/cruise phases across nearby flights", 0.26],
        ],
        confidence: "Medium",
      };
    }
    const exposure = chtExposureNote({
      aboveReviewThreshold: metric.details.aboveReviewThreshold,
      belowCautionThreshold: metric.details.belowCautionThreshold,
      peak: metric.details.max,
      score: metric.score,
    });
    return {
      title: "CHT",
      body: `${chtPurpose}\n\n${exposure}. That stays well inside the time-above-threshold range this screen treats as normal.`,
      causes: [
        ["CHT below current review thresholds", 0.48],
        ["No large sustained hot-cylinder pattern", 0.32],
        ["Compare CHT in similar climb/cruise phases across nearby flights", 0.2],
      ],
      confidence: "Medium",
    };
  }
  if (key === "chtRhythm") {
    const windows = metric.details?.windows ?? 0;
    const rhythmPurpose = "This screen looks for repeating CHT-only movement during stable loaded operation. A CHT trace that pulses without matching fuel-flow, MAP, RPM, or same-cylinder EGT movement usually belongs in probe, connector, harness, bayonet, or local cooling-air review before engine-condition conclusions.";
    return {
      title: "CHT rhythm",
      body: `${rhythmPurpose}\n\nThe check reviewed ${windows} stable window${windows === 1 ? "" : "s"} without a CHT-only rhythm strong enough to flag.`,
      causes: [
        ["No repeating isolated CHT movement above threshold", 0.48],
        ["Stable power gates were used before judging rhythm", 0.3],
        ["Brief sub-threshold CHT wiggles can still be reviewed in the raw graph if a pilot noticed them", 0.22],
      ],
      confidence: windows ? "Medium" : "Low",
    };
  }
  if (key === "egt") {
    const hot = metric.details.hot;
    const egtBalancePurpose = "This screen looks at how much EGT varies between cylinders during steady operation. Larger persistent EGT spread often reflects mixture-distribution differences — uneven fuel-injector flow, an induction leak biased to one cylinder, or combustion-timing differences across the bank — so it is a useful health and tuning signal even when nothing is wrong mechanically.";
    if (hot) {
      const hotLabel = readableProbeLabel(hot.key) || readableProbeLabel(hot.label) || hot.label || "EGT";
      return {
        title: "EGT balance",
        body: `${egtBalancePurpose}\n\n${hotLabel} had an isolated high EGT sample with a peak of ${Math.round(hot.max)} F, but it did not persist long enough to count as a balance issue on its own. Absolute EGT varies by installation, so compare the raw trace before treating a single point as engine evidence.`,
        causes: [
          ["Isolated high EGT sample did not persist", 0.38],
          ["Single-sample spikes can be probe or logger artifacts", 0.34],
          ["Repeatability across future flights matters most", 0.28],
        ],
        confidence: "Low",
      };
    }
    const idleContext = metric.details?.idleContext;
    if (idleContext?.active) {
      const powerDetails = [
        Number.isFinite(idleContext.rpmMedian) ? `RPM about ${Math.round(idleContext.rpmMedian)}` : "",
        Number.isFinite(idleContext.fuelFlowMedian) ? `fuel flow about ${idleContext.fuelFlowMedian.toFixed(1)} GPH` : "",
        Number.isFinite(idleContext.mapMedian) ? `MAP about ${idleContext.mapMedian.toFixed(1)} inHg` : "",
      ].filter(Boolean).join(", ");
      return {
        title: "EGT balance",
        body: `${egtBalancePurpose}\n\nThe widest EGT separation here happened during idle or very low power${powerDetails ? ` (${powerDetails})` : ""}. That is common context and carries much less weight than a repeatable gap during stable loaded operation. Typical larger differences were about ${Math.round(metric.details.p90 ?? 0)} F across cylinders, with a maximum momentary gap of ${Math.round(metric.details.max ?? 0)} F.`,
        causes: [
          ["EGT spread was concentrated at idle or very low power", 0.44],
          ["No stronger loaded-operation EGT balance cue", 0.32],
          ["Compare matching climb or cruise phases across nearby flights", 0.24],
        ],
        confidence: "Medium",
      };
    }
    return {
      title: "EGT balance",
      body: `${egtBalancePurpose}\n\nSustained EGT spread in this log stayed within the balance screen's threshold. Typical larger differences were about ${Math.round(metric.details.p90 ?? 0)} F across cylinders, with a maximum momentary gap of ${Math.round(metric.details.max ?? 0)} F.`,
      causes: [
        ["Sustained EGT spread stayed below the balance screen's threshold", 0.46],
        ["No cylinder consistently broke away from the rest", 0.3],
        ["Dedicated lean-of-peak or rich-of-peak comparisons in nearby flights would tighten this read", 0.24],
      ],
      confidence: "Medium",
    };
  }
  if (key === "valve") {
    const top = metric.details?.top ?? {};
    const best = top.best ?? {};
    const cylinderRef = findingCylinderReference(top.cylinder);
    const frequency = valveFrequencyLabel(best.frequency);
    const amplitude = Number.isFinite(best.amplitude) ? `${Math.round(best.amplitude)} F peak-to-peak` : "";
    const cycle = Number.isFinite(best.frequency?.cycleSeconds) ? `about every ${Math.round(best.frequency.cycleSeconds)} sec` : "";
    const ratioMultiplier = Number.isFinite(best.ratio) ? `${best.ratio.toFixed(1)}x` : "";
    const ratioPhrase = ratioMultiplier ? `${ratioMultiplier} the median swing of the other cylinders` : "";
    const rotaxRhythm = metric.details?.rhythmProfile?.engineFamily === "rotax";
    const purpose = rotaxRhythm
      ? "This screen looks for repeating EGT instability during stable loaded operation. For Rotax engines it is treated as a lower-confidence combustion, ignition, induction, or data-quality cue rather than a direct exhaust-valve interpretation."
      : "This screen looks for signs of a possible failing exhaust valve — specifically, one cylinder's EGT swinging up and down on a repeating cycle, which can show up when a valve is sticking or burning.";
    const observed = Number.isFinite(best.amplitude) && Number.isFinite(top.cylinder)
      ? `${purpose}\n\n${capitalizeSentence(cylinderRef.label)} had the strongest signal at about ${amplitude}${cycle ? ` ${cycle}` : ""}${ratioPhrase ? `, roughly ${ratioPhrase}` : ""}. That is well below the swing size and repeat strength that would warrant a closer look.`
      : "";
    return {
      title: "EGT rhythm",
      body: `${observed || `${purpose}\n\nAcross ${metric.details.windows ?? 0} steady-power window${metric.details.windows === 1 ? "" : "s"}, no cylinder showed a strong repeating EGT cycle relative to its peers.`}`,
      evidenceTitle: "Observed pattern",
      metaItems: [
        amplitude ? `EGT swing ${amplitude}` : "",
        frequency && frequency !== "No dominant frequency band" ? `Cycle rate ${frequency}` : "",
        ratioMultiplier ? `Vs other cylinders ${ratioMultiplier} median swing` : "",
        `${metric.details.windows ?? 0} steady-power window${metric.details.windows === 1 ? "" : "s"} analyzed`,
      ].filter(Boolean),
      evidence: [
        Number.isFinite(top.cylinder) ? { label: "Cylinder", value: cylinderRef.label, detail: "Cylinder with the largest EGT swing during this analysis" } : null,
        amplitude ? { label: "EGT swing", value: amplitude, detail: "Peak-to-peak swing measured during steady-power segments" } : null,
        frequency && frequency !== "No dominant frequency band" ? { label: "Cycle rate", value: frequency, detail: "Dominant cycle rate during the steadiest power segment" } : null,
        ratioMultiplier ? { label: "Vs other cylinders", value: `${ratioMultiplier} median swing`, detail: "Compared with the median cylinder EGT swing in the same window" } : null,
      ].filter(Boolean),
      causes: [
        [rotaxRhythm ? "EGT swings stayed below the Rotax instability review threshold" : "EGT swings stayed below the review threshold at both slow and fast cycle rates", 0.5],
        ["Checked during steady-power segments across multiple cycle rates", 0.32],
        ["Repeat behavior across future flights matters most for this kind of finding", 0.18],
      ],
      confidence: rotaxRhythm ? "Low" : "Medium",
    };
  }
  if (key === "combustion") {
    const steady = metric.details.steadyEvidence ?? {};
    const windowsCount = metric.details.settledWindows ?? steady.windows ?? metric.details.windows ?? 0;
    const windowsLabel = `${windowsCount} settled-power window${windowsCount === 1 ? "" : "s"}`;
    const spreadClause = Number.isFinite(steady.egtSpreadF)
      ? ` The typical EGT spread was about ${steady.egtSpreadF} F; EGT Balance evaluates that separately.` : "";
    return {
      title: "CHT/EGT pairing",
      body: `This screen compares same-cylinder CHT and EGT movement after power and mixture have settled. Across ${windowsLabel}, no paired pattern crossed the review threshold.${spreadClause} This result applies to the paired channels and intervals evaluated.`,
      metaItems: [`${windowsLabel} analyzed`],
      evidence: detectorCoverageEvidence(metric),
      causes: [
        ["No paired CHT/EGT pattern matched the screen's signatures", 0.46],
        ["Paired temperatures and power were available in the evaluated windows", 0.3],
        ["Repeatability across future logs still matters", 0.24],
      ],
      confidence: metric.details.confidence?.label ?? "Low",
    };
  }
  if (key === "divergence") {
    return {
      title: "Transient divergence",
      body: `This screen looks for short events where one cylinder's EGT or CHT briefly drifts away from the pack and then snaps back. Quick fan-outs like that can be early signs of intermittent issues — a plug fouling and clearing, an ignition lead or boot starting to fail, a valve beginning to stick, a brief air-fuel anomaly, or an instrumentation glitch — that don't stay long enough to show up on the sustained-pattern screens.\n\nNo transient divergence event in this log rose above the review threshold.`,
      causes: [
        ["No transient cylinder fan-out crossed the review threshold", 0.43],
        ["Power and flight context explain the small spreads that did occur", 0.32],
        ["Brief intermittents can still hide in the raw trace — worth a glance if a related pattern repeats", 0.25],
      ],
      confidence: "Medium",
    };
  }
  if (key === "descentEgtDropout") {
    return {
      title: "Descent EGT dropout",
      body: "At low power — descent or a logged low-power transition — single-cylinder issues are easier to see. This screen watches for one EGT trace dropping sharply away from the rest during that phase. Possible explanations include a sticking exhaust valve, a partial injector restriction, plug fouling that doesn't clear cleanly, or an instrumentation glitch.\n\nNo EGT trace in this log dropped sharply enough during descent or a low-power transition to cross the review threshold.",
      causes: [
        ["No EGT trace dropped sharply enough to cross the review threshold", 0.45],
        ["Logged fuel-flow, MAP, RPM, phase, and altitude context did not align with a dropout pattern", 0.32],
        ["Repeatability across future descents still matters", 0.23],
      ],
      confidence: "Medium",
    };
  }
  if (key === "inductionLeak") {
    return {
      title: "Induction leak",
      body: `${metric.details.top ? "A cylinder-specific EGT response was retained below the selected sensitivity's review threshold." : "No induction-leak cue exceeded the review threshold in the available evidence."} The screen reviews cylinder-specific EGT responses during power reductions and, when usable, between settled higher- and lower-MAP periods. Missing channels and unknown mixture settings limit this review; absence of a cue does not exclude a leak.`,
      causes: [
        ["No qualifying cylinder-specific response in the evaluated evidence", 0.5],
        ["Operating conditions and mixture setting limit the comparison", 0.5],
      ],
      confidence: metric.details.confidence?.label ?? "Low",
    };
  }
  if (key === "mag") {
    const top = metric.details.top ?? {};
    const window = magWindowLabel(top);
    const candidateContext = top.mode === "runupBlock"
      ? `after RPM came up ${Math.round(top.rpmIncrease ?? 0)} RPM to runup power`
      : `with about ${Math.round(top.rpmDrop ?? 0)} RPM drop`;
    return {
      title: "Mag check",
      body: `The mag-check screen found a likely ignition-test candidate${window ? ` at ${window}` : ""} in ${top.phase ?? "the log"} ${candidateContext}. It screened for the expected mag-check response: RPM drop where visible and EGT rise on the checked cylinders. Median EGT rise was ${Math.round(top.medianRise ?? 0)} F, and the cylinder EGT responses were consistent by the current thresholds.${metric.details.confidence ? ` Candidate confidence: ${metric.details.confidence}.` : ""} ${(metric.details.confidenceReasons ?? []).join(" ")}`,
      causes: [
        ["No weak cylinder response detected in candidate segment", 0.42],
        ["Check coverage and available power channels limit the interpretation", 0.3],
        ["Explicit magneto-switch annotations would improve confidence", 0.28],
      ],
      confidence: "Low",
    };
  }
  if (key === "carbIce") {
    const riskText = metric.details.riskP95 >= 0.5
      ? " Carb temperature entered the screen's icing-risk range. Temperature alone does not establish carb ice." : "";
    return {
      title: "Carb ice",
      body: `The carb-ice screen used synchronized carb temperature, OAT, and RPM or MAP during partial-power running. No sustained power-decay or carb-heat recovery signature crossed the review threshold in those windows.${riskText}`,
      evidence: detectorCoverageEvidence(metric),
      causes: [
        ["No coupled power-loss or recovery cue crossed the review threshold", 0.44],
        ["Carb temperature, OAT, and power were available together", 0.3],
        ["Pilot carb-heat context would still improve interpretation", 0.26],
      ],
      confidence: metric.details.confidence?.label ?? "Low",
    };
  }
  if (key === "cooling") {
    const top = metric.details.top ?? {};
    return {
      title: "Cooling",
      body: `The cooling screen compared CHTs against the other cylinders and looked for CHT rise without matching EGT or power movement. The strongest high-temperature difference was about ${Math.round(top.p95Delta ?? 0)} F, which does not exceed the current review level.`,
      causes: [
        ["No cooling-pattern CHT separation above threshold", 0.43],
        ["Paired CHT/EGT screen did not escalate", 0.3],
        ["Airspeed context used when available", 0.27],
      ],
      confidence: metric.details.speedKey ? "Medium" : "Low",
    };
  }
  if (key === "coolingRate") {
    const top = metric.details.top ?? {};
    const maxRate = Math.round(top.rateFpm ?? 0);
    const threshold = Math.round(metric.details.thresholdFpm ?? chtCoolingRateTriggerFpm);
    const coolingRatePurpose = "This screen looks for cylinders cooling rapidly enough to be worth a second look. Sudden CHT drops can come from power reductions, high-airspeed descents, or a probe issue, and some operators also monitor them for shock-cooling concerns over time. Startup and shutdown rollout are excluded since rapid cooling there is expected.";
    if (metricNearCoolingRateTrigger(metric)) {
      return {
        title: "Cooling rate",
        body: `${coolingRatePurpose}\n\nThe fastest observed rate in this log was ${maxRate} F/min — close to, but not crossing, the ${threshold} F/min review trigger. Worth keeping an eye on if the pattern repeats in nearby flights.`,
        causes: [
          ["Cooling rate stayed below the review trigger", 0.48],
          ["The fastest cooling sat close to the trigger — worth watching for repeats", 0.28],
          ["Brief steep descents can still warrant a glance at the raw CHT trace", 0.24],
        ],
        confidence: "Medium",
      };
    }
    return {
      title: "Cooling rate",
      body: `${coolingRatePurpose}\n\nThe fastest observed rate in this log was ${maxRate} F/min, well below the ${threshold} F/min review trigger.`,
      causes: [
        ["No cylinder cooling rate crossed the review trigger", 0.48],
        ["Startup and shutdown phases are excluded", 0.24],
        ["Brief steep descents can still warrant a glance at the raw CHT trace", 0.28],
      ],
      confidence: "Medium",
    };
  }
  if (key === "charging") {
    const charging = metric.details ?? {};
    const recovered = Number.isFinite(charging.recoveryMedian) ? `${charging.recoveryMedian.toFixed(1)} V` : "the expected range";
    const hasAmpConfirmation = Boolean(charging.amps?.available && charging.amps.hasSpike);
    const ampText = hasAmpConfirmation
      ? ` and the current channel showed ${charging.amps.note}`
      : ", but this log does not include an amps channel to confirm the recharge spike";
    return {
      title: "Start charge",
      body: `The start-charge screen detected an engine-start event and saw bus voltage recover to ${recovered}${ampText}.`,
      causes: hasAmpConfirmation
        ? [
            ["Post-start charging recovery detected", 0.44],
            ["Current spike confirms recharge signature for this log", 0.34],
            ["Running electrical behavior is handled by the Electrical card", 0.22],
          ]
        : [
            ["Post-start bus voltage recovered promptly", 0.48],
            ["Amps channel not available for recharge-spike confirmation", 0.32],
            ["Running electrical behavior is handled by the Electrical card", 0.2],
          ],
      confidence: hasAmpConfirmation ? "Medium" : "Low",
    };
  }
  if (key === "abnormalCombustion") {
    const egtBalanceCue = metric.details?.egtBalanceCue;
    if (egtBalanceCue) {
      const label = egtBalanceCue.label || "EGT";
      const deviationClause = Number.isFinite(egtBalanceCue.p90DeviationF)
        ? ` about ${Math.round(egtBalanceCue.p90DeviationF)} F below its peers`
        : " below its peers";
      return {
        title: "Abnormal combustion",
        body: `EGT Balance is already calling out a sustained ${label} drop${deviationClause}. This screen asks a different question: did the same cylinder's CHT climb rapidly or pull away from the other CHTs while that EGT stayed low? It did not see that CHT corroboration in the high-power windows it could evaluate, so this is not being treated as an abnormal-combustion cue.`,
        causes: [
          ["EGT-only event is handled by EGT Balance", 0.38],
          ["No same-cylinder CHT runaway or peer divergence detected", 0.4],
          ["Recheck if the EGT drop repeats with matching CHT heat on nearby flights", 0.22],
        ],
        evidence: detectorCoverageEvidence(metric),
        confidence: metric.details.confidence?.label ?? "Low",
      };
    }
    return {
      title: "Abnormal combustion",
      body: "At high power this screen looks for rapid CHT rise with flat or falling EGT on the same cylinder. No pattern crossed the review threshold in the paired high-power data evaluated. This is a screening result for those intervals, not confirmation that an intermittent event could not have occurred elsewhere.",
      evidence: detectorCoverageEvidence(metric),
      causes: [
        ["No single-cylinder CHT runaway with decoupled EGT crossed the review threshold", 0.46],
        ["Paired temperatures and high-power context were available together", 0.32],
        ["Compare nearby flights if symptoms or a related pattern repeat", 0.22],
      ],
      confidence: metric.details.confidence?.label ?? "Low",
    };
  }

  return {
    title: "Electrical",
    body: `Running bus voltage stayed near the expected range, from ${Number(metric.details.runningMin ?? metric.details.min ?? 0).toFixed(1)} V to ${Number(metric.details.runningMax ?? metric.details.max ?? 0).toFixed(1)} V.`,
    causes: [
      ["Running voltage stayed inside current screen", 0.48],
      ["No sustained alternator/regulator excursion detected", 0.32],
      ["Start voltage is judged separately", 0.2],
    ],
    confidence: "Medium",
  };
}
